import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, Send, Loader2, Bot, Trash2, Feather, ArrowRight } from "lucide-react";
import api from "../lib/api";

/**
 * Floating "Chief of Staff" assistant.
 * - Bottom-right pill button on every authed page.
 * - Streams from POST /api/assistant/stream (SSE).
 * - Session id lives in localStorage so a conversation survives page navigation.
 * - Page context is the current pathname — assistant tailors suggestions to it.
 */
const LS_HISTORY = "cos_history_v1";
const LS_SESSION = "cos_session_id_v1";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ChiefOfStaff() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [starters, setStarters] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // Load history + starters on mount
  useEffect(() => {
    try {
      const h = JSON.parse(localStorage.getItem(LS_HISTORY) || "[]");
      if (Array.isArray(h)) setMessages(h);
    } catch (_) {}
    api.get("/assistant/starters").then(({ data }) => setStarters(data.starters || [])).catch(() => {});
  }, []);

  // Persist history (skip when empty so localStorage stays cleared)
  useEffect(() => {
    try {
      if (messages.length === 0) return;
      localStorage.setItem(LS_HISTORY, JSON.stringify(messages.slice(-40)));
    } catch (_) {}
  }, [messages]);

  // Autoscroll on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const pageContext = location.pathname;
  const canSend = input.trim().length > 0 && !streaming;

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    const userMsg = { role: "user", content: text };
    const asstIdx = messages.length + 1;
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    setStreaming(true);

    const token = localStorage.getItem("token");
    const session_id = localStorage.getItem(LS_SESSION) || null;
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API_BASE}/api/assistant/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: [...messages, userMsg].map((m) => ({ role: m.role, content: m.content })),
          page_context: pageContext,
          session_id,
        }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const chunk of parts) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trim();
          if (raw === "[DONE]") continue;
          try {
            const ev = JSON.parse(raw);
            if (ev.type === "delta") {
              setMessages((m) => {
                const next = [...m];
                if (next[asstIdx]) next[asstIdx] = { ...next[asstIdx], content: next[asstIdx].content + ev.content };
                return next;
              });
            } else if (ev.type === "meta" && ev.session_id) {
              localStorage.setItem(LS_SESSION, ev.session_id);
            } else if (ev.type === "error") {
              setMessages((m) => {
                const next = [...m];
                if (next[asstIdx]) next[asstIdx] = { ...next[asstIdx], content: `⚠️ ${ev.error}` };
                return next;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setMessages((m) => {
          const next = [...m];
          if (next[asstIdx]) next[asstIdx] = { ...next[asstIdx], content: `⚠️ ${e.message || "Assistant offline"}` };
          return next;
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const stop = () => { abortRef.current?.abort(); };
  const clear = () => {
    setMessages([]);
    localStorage.removeItem(LS_HISTORY);
    localStorage.removeItem(LS_SESSION);
  };

  const contextLabel = useMemo(() => prettyContext(pageContext), [pageContext]);

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-40 bottom-5 right-5 brut-btn flex items-center gap-2 pl-3 pr-4 py-2.5 group"
          style={{ boxShadow: "5px 5px 0 #B45309" }}
          data-testid="cos-open-btn"
        >
          <div className="w-7 h-7 rounded-lg border-2 border-slate-900 bg-amber-300 flex items-center justify-center">
            <Feather size={14} strokeWidth={2.5} className="text-slate-900" />
          </div>
          <span className="font-heading font-bold text-sm">Chief of Staff</span>
          <span className="hidden group-hover:inline-block text-[10px] uppercase tracking-widest font-heading font-bold text-amber-200 bg-slate-800 border border-amber-300 rounded-full px-1.5 py-0.5">ask me</span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div
          className="fixed z-50 bottom-5 right-5 w-[92vw] max-w-[400px] h-[min(70vh,640px)] brut-card p-0 flex flex-col bg-white overflow-hidden"
          style={{ boxShadow: "8px 8px 0 #B45309", borderColor: "#0F172A" }}
          data-testid="cos-drawer"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-2 border-b-2 border-slate-900 bg-amber-50">
            <div className="w-9 h-9 rounded-lg border-2 border-slate-900 bg-amber-300 flex items-center justify-center">
              <Feather size={16} strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm leading-tight">Medley</div>
              <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500">Your AI Chief of Staff · {contextLabel}</div>
            </div>
            {messages.length > 0 && (
              <button onClick={clear} className="text-slate-400 hover:text-red-500 p-1" title="Clear conversation" data-testid="cos-clear-btn">
                <Trash2 size={14} strokeWidth={2.5} />
              </button>
            )}
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-900 p-1" title="Close" data-testid="cos-close-btn">
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="cos-body">
            {messages.length === 0 ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-xl border-2 border-slate-900 bg-amber-100 mx-auto flex items-center justify-center mb-3">
                  <Sparkles size={22} strokeWidth={2.5} className="text-amber-700" />
                </div>
                <div className="font-heading font-bold text-base mb-1">How can I help today?</div>
                <p className="text-xs text-slate-500 max-w-[260px] mx-auto mb-4 leading-relaxed">
                  I'll pick the right brain, sketch a workflow, or suggest a debate topic — tailored to what you're doing.
                </p>
                <div className="grid gap-2" data-testid="cos-starters">
                  {starters.map((s, i) => (
                    <button
                      key={s.prompt || s.label || `starter-${i}`}
                      onClick={() => send(s.prompt)}
                      className="text-left px-3 py-2 rounded-xl border-2 border-slate-900 bg-white text-xs font-heading font-semibold hover:bg-amber-50 transition-colors"
                      data-testid={`cos-starter-${i}`}
                    >
                      <span className="text-amber-700">→</span> {s.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => <Bubble key={m.message_id || `msg-${i}`} m={m} streaming={streaming && i === messages.length - 1 && m.role === "assistant"} />)
            )}
          </div>

          {/* Input */}
          <div className="border-t-2 border-slate-900 bg-white p-3">
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-end gap-2"
            >
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={1}
                placeholder="Ask about brains, workflows, or debates…"
                className="brut-input text-sm resize-none max-h-[100px] py-2"
                data-testid="cos-input"
                disabled={streaming}
              />
              {streaming ? (
                <button type="button" onClick={stop} className="brut-btn brut-btn-outline text-xs px-3 py-2" title="Stop" data-testid="cos-stop-btn">
                  <Loader2 size={14} strokeWidth={2.5} className="animate-spin" />
                </button>
              ) : (
                <button type="submit" disabled={!canSend} className="brut-btn text-xs px-3 py-2" title="Send" data-testid="cos-send-btn">
                  <Send size={14} strokeWidth={2.5} />
                </button>
              )}
            </form>
            <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-400 mt-1.5 text-center">
              Powered by Claude Sonnet 4.6 · Enter to send · Shift+Enter for newline
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* -------------------- Bubble -------------------- */
function Bubble({ m, streaming }) {
  const isUser = m.role === "user";
  const { text, actions } = useMemo(() => parseActions(m.content || ""), [m.content]);
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`cos-msg-${m.role}`}>
      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 leading-relaxed ${
          isUser
            ? "bg-slate-900 text-white"
            : "bg-amber-50 border-2 border-slate-900 text-slate-800"
        }`}
      >
        {!isUser && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <Bot size={11} strokeWidth={2.5} className="text-amber-700" />
            <span className="text-[9px] uppercase tracking-widest font-heading font-bold text-amber-700">Medley</span>
          </div>
        )}
        <div className="text-sm leading-relaxed">
          <MarkdownLite text={text || (streaming ? " " : "")} />
        </div>
        {streaming && !m.content && (
          <div className="flex items-center gap-1 mt-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600 dot-blink" />
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600 dot-blink" style={{ animationDelay: "0.2s" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600 dot-blink" style={{ animationDelay: "0.4s" }} />
          </div>
        )}
        {!isUser && actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2 border-t-2 border-amber-200" data-testid="cos-actions">
            {actions.map((a, i) => <ActionButton key={a.href || a.label || `action-${i}`} action={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------- Action tag parser + button -------------------- */
// Match [[action:kind|k=v|k=v|...]] — allow any char inside params except '|' or ']'
const ACTION_RE = /\[\[action:([a-z0-9-]+)((?:\|[^|\]]+)*)\]\]/g;

function parseActions(content) {
  if (!content) return { text: "", actions: [] };
  const actions = [];
  const text = content.replace(ACTION_RE, (_, kind, tail) => {
    const params = {};
    (tail || "").split("|").filter(Boolean).forEach((kv) => {
      const eq = kv.indexOf("=");
      if (eq === -1) return;
      const k = kv.slice(0, eq).trim();
      const v = kv.slice(eq + 1).trim();
      if (k) params[k] = v;
    });
    actions.push({ kind, params });
    return ""; // strip the raw tag from displayed text
  }).replace(/\n{3,}/g, "\n\n").trim();
  return { text, actions };
}

function ActionButton({ action }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const label = action.params.label || defaultLabel(action);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      switch (action.kind) {
        case "create-worker":
          navigate("/workforce?new=1"); break;
        case "open-workflows":
          navigate("/workforce/workflows"); break;
        case "open-workflow-templates":
          navigate("/workforce/templates"); break;
        case "open-workflow-template": {
          const slug = action.params.slug;
          if (!slug) throw new Error("Missing template slug");
          const { data } = await api.post(`/workflow_templates/${slug}/use`);
          navigate(`/workforce/workflows/${data.workflow_id}`);
          break;
        }
        case "open-debate":
          navigate("/workforce/debate"); break;
        case "open-brain": {
          const id = action.params.id || "";
          navigate(`/models${id ? `#${id}` : ""}`); break;
        }
        case "clone-brain-workflow": {
          const id = action.params.id;
          if (!id) throw new Error("Missing brain id");
          const { data } = await api.post(`/workflow_templates/from_brain/${id}`);
          navigate(`/workforce/workflows/${data.workflow_id}`);
          break;
        }
        default:
          throw new Error(`Unknown action: ${action.kind}`);
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 text-amber-200 border-2 border-amber-300 text-xs font-heading font-bold hover:bg-amber-300 hover:text-slate-900 transition-colors disabled:opacity-50"
      data-testid={`cos-action-${action.kind}`}
      title={action.kind}
    >
      {busy ? <Loader2 size={11} strokeWidth={2.5} className="animate-spin" /> : <ArrowRight size={11} strokeWidth={2.5} />}
      {label}
    </button>
  );
}

function defaultLabel(a) {
  switch (a.kind) {
    case "create-worker": return "Create worker";
    case "open-workflows": return "Open Workflows";
    case "open-workflow-templates": return "Browse templates";
    case "open-workflow-template": return "Use this template";
    case "open-debate": return "Start a debate";
    case "open-brain": return "See brain";
    case "clone-brain-workflow": return "Clone starter workflow";
    default: return a.kind;
  }
}

/* -------------------- Minimal Markdown -------------------- */
function MarkdownLite({ text }) {
  if (!text) return null;
  // Handles **bold**, `inline code`, and single line breaks. Keeps it tiny to avoid deps.
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <span className="whitespace-pre-wrap">
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) return <b key={i}>{p.slice(2, -2)}</b>;
        if (p.startsWith("`") && p.endsWith("`")) {
          return <code key={i} className="bg-slate-100 border border-slate-300 rounded px-1 text-[12px] font-mono">{p.slice(1, -1)}</code>;
        }
        return <React.Fragment key={i}>{p}</React.Fragment>;
      })}
    </span>
  );
}

/* -------------------- Context labels -------------------- */
function prettyContext(path) {
  if (!path) return "Dashboard";
  if (path === "/" || path.startsWith("/dashboard")) return "Dashboard";
  if (path.startsWith("/chat")) return "Chat";
  if (path.startsWith("/compare")) return "Compare";
  if (path.startsWith("/research")) return "Research";
  if (path.startsWith("/documents")) return "Documents";
  if (path.startsWith("/agents")) return "Quick Agents";
  if (path.startsWith("/models")) return "Brains catalog";
  if (path.startsWith("/workforce/workflows/")) return "Workflow editor";
  if (path.startsWith("/workforce/workflows")) return "Workflows";
  if (path.startsWith("/workforce/runs/")) return "Run detail";
  if (path.startsWith("/workforce/runs")) return "Runs";
  if (path.startsWith("/workforce/debate")) return "Debate arena";
  if (path.startsWith("/workforce/teams")) return "Teams";
  if (path.startsWith("/workforce/templates")) return "Templates";
  if (path.startsWith("/workforce/analytics")) return "Analytics";
  if (path.startsWith("/workforce/providers")) return "Providers";
  if (path.startsWith("/workforce")) return "Workforce";
  return "AiMedley";
}
